import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { differenceInYears } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useLabels } from '../../contexts/LabelsContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
// import * as AppPropTypes from '../../lib/PropTypes';
import SubmitButton from '../buttons/SubmitButton';
import DateField from '../fields/DateSeparated';
import FieldLabel from '../fields/FieldLabel';
import ProvinceField from '../fields/Province';

import styles from '../../../styles/partials/age-gate-default.module.scss';

import provinces from '../../data/provinces.json';

const propTypes = {
    defaultDrinkingAge: PropTypes.number,
    disabledProvinces: PropTypes.arrayOf(PropTypes.string),
    provinceId: PropTypes.string,
    birthdate: PropTypes.string,
    onProvinceChange: PropTypes.func,
    onBirthdateChange: PropTypes.func,
    onSuccess: PropTypes.func,
    submitClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    inputDateClassName: PropTypes.string,
    className: PropTypes.string,
};

const defaultProps = {
    defaultDrinkingAge: 18,
    disabledProvinces: null,
    provinceId: null,
    birthdate: null,
    onProvinceChange: null,
    onBirthdateChange: null,
    onSuccess: null,
    submitClassName: null,
    inputClassName: null,
    inputDateClassName: null,
    className: null,
};

function AgeGateDefault({
    defaultDrinkingAge,
    disabledProvinces,
    provinceId,
    birthdate,
    onProvinceChange,
    onBirthdateChange,
    onSuccess,
    submitClassName,
    inputClassName,
    inputDateClassName,
    className,
}) {
    const themeClassName = useThemeClassName(styles);
    const [showAgeError, setShowAgeError] = useState(false);
    const { ageGateMessage = null, ageGateButton = null } = useLabels();

    const province = useMemo(
        () => provinces.find(({ id }) => id === provinceId) || null,
        [provinceId, provinces],
    );

    const { drinkingAge = defaultDrinkingAge } = province || {};
    const onProvinceChangePrivate = useCallback(
        (newValue) => {
            if (onProvinceChange !== null) {
                onProvinceChange(newValue);
            }
        },
        [onProvinceChange],
    );

    const onBirthdateChangePrivate = useCallback(
        (date) => {
            if (onBirthdateChange !== null) {
                onBirthdateChange(date);
            }
        },
        [onBirthdateChange],
    );

    const onButtonClick = useCallback(() => {
        setShowAgeError(false);
        setTimeout(() => {
            const validProvince = provinceId !== null;
            const birthDateFilled = birthdate !== null;
            if (validProvince && birthDateFilled) {
                if (differenceInYears(new Date(), new Date(birthdate)) >= drinkingAge) {
                    if (onSuccess !== null) {
                        onSuccess({ provinceId, birthdate });
                    }
                } else {
                    setShowAgeError(true);
                }
            } else {
                setShowAgeError(true);
            }
        }, 1);
    }, [provinceId, birthdate, onSuccess]);

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className !== null,
                    [styles.showAgeError]: showAgeError,
                },
            ])}
        >
            {ageGateMessage !== null ? (
                <div
                    className={styles.message}
                    // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
                    dangerouslySetInnerHTML={{ __html: ageGateMessage }}
                ></div>
            ) : null}

            <div className={styles.field}>
                <FieldLabel className={styles.label}>
                    <FormattedMessage
                        defaultMessage="Select your province"
                        description="Age gate field label"
                    />
                </FieldLabel>
                <ProvinceField
                    className={classNames([
                        styles.input,
                        {
                            [inputClassName]: inputClassName !== null,
                        },
                    ])}
                    value={provinceId !== null ? provinceId : ''}
                    disabledProvinces={disabledProvinces}
                    onChange={onProvinceChangePrivate}
                    required
                />
            </div>
            <div className={styles.field}>
                <FieldLabel className={styles.label}>
                    <FormattedMessage
                        defaultMessage="Select your birthday"
                        description="Age gate field label"
                    />
                </FieldLabel>
                <DateField
                    className={classNames([
                        styles.input,
                        {
                            [inputClassName]: inputClassName !== null,
                            [inputDateClassName]: inputDateClassName !== null,
                        },
                    ])}
                    value={birthdate}
                    onChange={onBirthdateChangePrivate}
                    required
                />
            </div>
            <div className={styles.ageError}>
                <FormattedMessage
                    defaultMessage="You must be of legal drinking age to enter"
                    description="Age gate error label"
                />
            </div>
            <SubmitButton
                type="button"
                className={classNames([
                    styles.button,
                    { [submitClassName]: submitClassName !== null },
                ])}
                onClick={() => {
                    onButtonClick();
                }}
            >
                <span className={styles.label}>
                    {ageGateButton || (
                        <FormattedMessage defaultMessage="Next" description="Button label" />
                    )}
                </span>
                <span className={styles.arrow}>
                    <FontAwesomeIcon icon={faChevronRight} />
                </span>
            </SubmitButton>
        </div>
    );
}

AgeGateDefault.propTypes = propTypes;
AgeGateDefault.defaultProps = defaultProps;

export default AgeGateDefault;
